import React from 'react';
import Modal from 'react-modal';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import { Field, reduxForm, SubmissionError } from 'redux-form';
import { connect } from 'react-redux';
import { renderCheckbox } from 'lib/helper/form';
import MultipleEmailInput from '../common/MultipleEmailInput';
import MASpinner from '../../common/MASpinner';
import InternalUserTypeahead from '../../fields/InternalUserTypeahead';
import { addToast } from 'lib/redux/actions/toast';
import { requests } from '../../../lib/helper/agent';
import * as API_ROUTES from '../../../lib/routing/api';
import { ContactRecordTypeCodes } from '../../../apiTypes';

Modal.setAppElement('#root');

const mapStateToProps = (state) => ({
  filters: state.invoices.filters,
});

const mapDispatchToProps = {
  addToast,
};

class HospitalBulkChaseForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      toEmails: [],
      ccUsers: [],
      clearTypeaheads: false,
      includePdfs: true,
      hospitalContactRecords: {}, // Changed to store contacts grouped by hospital ID
      loadingContactRecords: false,
      selectedInvoices: [],
      uniqueHospitalIds: [],
    };
  }

  componentDidMount() {
    if (this.props.isOpen) {
      this.fetchInvoicesAndContactRecords();
    }
  }

  componentDidUpdate(prevProps) {
    // Check if the form was opened or selected invoices changed
    if (this.props.isOpen && !prevProps.isOpen) {
      this.fetchInvoicesAndContactRecords();
    } else if (this.props.selectedInvoiceIds !== prevProps.selectedInvoiceIds && this.props.isOpen) {
      this.fetchInvoicesAndContactRecords();
    }
  }

  fetchInvoicesAndContactRecords = () => {
    const { selectedInvoiceIds } = this.props;

    if (!selectedInvoiceIds || selectedInvoiceIds.length === 0) {
      this.setState({
        selectedInvoices: [],
        uniqueHospitalIds: [],
        hospitalContactRecords: {},
      });
      return;
    }

    this.setState({ loadingContactRecords: true });

    // Fetch the selected invoices to get their hospital information
    const invoicePromises = selectedInvoiceIds.map((invoiceId) => requests.get(`/invoices/${invoiceId}`));

    Promise.all(invoicePromises)
      .then((invoices) => {
        // Extract unique hospital IDs from the invoices
        const hospitalIds = [...new Set(invoices.filter((invoice) => invoice.hospital && invoice.hospital['@id']).map((invoice) => invoice.hospital['@id']))];

        this.setState({
          selectedInvoices: invoices,
          uniqueHospitalIds: hospitalIds,
        });

        // Fetch contact records for all unique hospitals
        return this.fetchContactRecordsForHospitals(hospitalIds);
      })
      .catch((error) => {
        console.error('Failed to fetch invoice details:', error);
        this.setState({
          selectedInvoices: [],
          uniqueHospitalIds: [],
          hospitalContactRecords: {},
          loadingContactRecords: false,
        });
      });
  };

  fetchContactRecordsForHospitals = (hospitalIds) => {
    if (hospitalIds.length === 0) {
      this.setState({
        hospitalContactRecords: {},
        loadingContactRecords: false,
      });
      return Promise.resolve();
    }

    // Fetch hospital details for all unique hospitals
    const hospitalPromises = hospitalIds.map((hospitalId) =>
      requests
        .get(`/hospitals/${hospitalId.replace('/hospitals/', '')}`)
        .then((hospital) => ({ hospitalId, hospital }))
        .catch((error) => {
          console.error(`Failed to fetch hospital ${hospitalId}:`, error);
          return { hospitalId, hospital: null };
        }),
    );

    return Promise.all(hospitalPromises)
      .then((results) => {
        const hospitalContactRecords = {};

        results.forEach(({ hospitalId, hospital }) => {
          if (hospital && hospital.contactRecords) {
            const contacts = hospital.contactRecords.filter(
              (contact) => contact.contactRecordType.code === ContactRecordTypeCodes.Hospital && contact.notifyByEmail && contact.email && !contact.isDisabled,
            );
            hospitalContactRecords[hospitalId] = {
              hospitalName: hospital.name,
              contacts: contacts,
            };
          } else {
            hospitalContactRecords[hospitalId] = {
              hospitalName: 'Unknown Hospital',
              contacts: [],
            };
          }
        });

        this.setState({
          hospitalContactRecords,
          loadingContactRecords: false,
        });
      })
      .catch((error) => {
        console.error('Failed to fetch hospital contact records:', error);
        this.setState({
          hospitalContactRecords: {},
          loadingContactRecords: false,
        });
      });
  };

  onSubmit = (values) => {
    const { addToast, selectedInvoiceIds, onClose, reset } = this.props;
    const { toEmails, ccUsers, includePdfs, hospitalContactRecords } = this.state;

    if (!selectedInvoiceIds || selectedInvoiceIds.length === 0) {
      throw new SubmissionError({ _error: 'No invoices selected' });
    }

    // Get selected contact record IDs from all hospitals
    const selectedContactRecordIds = [];
    Object.values(hospitalContactRecords).forEach(({ contacts }) => {
      contacts.forEach((contact) => {
        if (values['contact-' + contact.id]) {
          selectedContactRecordIds.push(contact.id);
        }
      });
    });

    // Hospital contact selection is mandatory
    if (selectedContactRecordIds.length === 0) {
      throw new SubmissionError({ _error: 'At least one hospital contact must be selected' });
    }

    // Get CC emails from selected users
    const ccUserEmails = ccUsers.map((user) => user.email).filter((email) => email);

    const data = {
      invoices: selectedInvoiceIds,
      hospitalContactRecordIds: selectedContactRecordIds,
      additionalToEmails: toEmails,
      ccEmails: ccUserEmails,
      includePdfs: includePdfs,
      ccConsultant: !!values.ccConsultant,
      ccConsultantSecretary: !!values.ccConsultantSecretary,
    };

    return requests
      .post(API_ROUTES.INVOICE_HOSPITAL_BULK_CHASE, data)
      .then((response) => {
        addToast(`Successfully sent emails for ${response.invoicesUpdated} invoices to ${response.emailsSent} recipients`, true);
        this.resetForm();
        reset();
        onClose();
      })
      .catch((error) => {
        const errorMessage = error.response?.body?.message || 'Failed to send hospital bulk chase emails';
        throw new SubmissionError({ _error: errorMessage });
      });
  };

  handleToEmailsChange = (emails) => {
    this.setState({ toEmails: emails });
  };

  handleCcUsersChange = (users) => {
    this.setState({ ccUsers: users || [] });
  };

  handlePdfToggle = (e) => {
    this.setState({ includePdfs: e.target.checked });
  };

  typeaheadCleared = () => {
    if (this.state.clearTypeaheads) {
      this.setState({ clearTypeaheads: false });
    }
  };

  resetForm = () => {
    this.setState({
      toEmails: [],
      ccUsers: [],
      clearTypeaheads: true,
      includePdfs: true,
    });
  };

  render() {
    const { handleSubmit, isOpen, onClose, submitting, selectedInvoiceIds = [], error } = this.props;
    const { toEmails, ccUsers, includePdfs, hospitalContactRecords, loadingContactRecords, clearTypeaheads } = this.state;

    return (
      <Modal
        isOpen={isOpen}
        onRequestClose={onClose}
        style={{
          content: {
            maxWidth: '800px',
            width: '90%',
            height: 'fit-content',
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            transform: 'translate(-50%, -50%)',
            padding: '2rem',
            borderRadius: '8px',
            border: '1px solid #ccc',
            zIndex: '1000',
          },
          overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: '999',
          },
        }}
        shouldCloseOnOverlayClick={true}>
        <div className="hospital-batch-chase-form">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4>Send Hospital Chaser Email</h4>
            <Button variant="outline-secondary" onClick={onClose} size="sm">
              &times;
            </Button>
          </div>

          <div className="mb-3">
            <strong>Selected Invoices: </strong>
            <span className="badge badge-primary">{selectedInvoiceIds.length} invoices</span>
            {Object.keys(hospitalContactRecords).length > 0 && (
              <>
                <span className="ml-2">
                  <strong>Hospitals: </strong>
                </span>
                <span className="badge badge-info">{Object.keys(hospitalContactRecords).length} hospitals</span>
              </>
            )}
          </div>

          <Form onSubmit={handleSubmit(this.onSubmit)}>
            {error && <div className="alert alert-danger">{error}</div>}

            <div className="row">
              <div className="col-md-6">
                <h6 className="mt-3 mb-2">Hospital Contacts (TO Recipients) *</h6>
                {loadingContactRecords ? (
                  <div className="text-center py-3">
                    <MASpinner size="sm" /> Loading hospital contacts...
                  </div>
                ) : Object.keys(hospitalContactRecords).length > 0 ? (
                  <div className="hospital-contacts-selection">
                    {Object.entries(hospitalContactRecords).map(([hospitalId, { hospitalName, contacts }]) => (
                      <div key={hospitalId} className="mb-3">
                        <h6 className="text-primary mb-2">{hospitalName}</h6>
                        {contacts.length > 0 ? (
                          <div className="ml-3">
                            {contacts.map((contact) => (
                              <div className="mb-2" key={contact.id}>
                                <Field
                                  name={'contact-' + contact.id}
                                  id={'contact-' + contact.id}
                                  label={`${contact.name} (${contact.email})`}
                                  component={renderCheckbox}
                                />
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="ml-3 text-muted">
                            <small>No contacts available for this hospital</small>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="alert alert-info">No hospital contacts available for selection.</div>
                )}
              </div>
              <div className="col-md-6">
                <h6 className="mt-3 mb-2">Email Options</h6>
                <MultipleEmailInput
                  label="Additional TO Email Addresses"
                  placeholder="Enter additional recipient email address"
                  value={toEmails}
                  onChange={this.handleToEmailsChange}
                  disabled={submitting}
                />

                <InternalUserTypeahead
                  label="CC Mediaccounts Staff"
                  multiple={true}
                  selectedUsers={ccUsers}
                  onChange={this.handleCcUsersChange}
                  shouldClear={clearTypeaheads}
                  hasCleared={this.typeaheadCleared}
                  rolesToShow='["ROLE_ADMIN","ROLE_SENIOR_STAFF","ROLE_STAFF","ROLE_CHASER_OUTSOURCER"]'
                />

                <Field name="ccConsultant" id="ccConsultant" label="CC Consultant" component={renderCheckbox} />
                <Field name="ccConsultantSecretary" id="ccConsultantSecretary" label="CC Consultant Secretary" component={renderCheckbox} />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6"></div>
              <div className="col-md-6">
                <h6 className="mt-3 mb-2">Attachment Options</h6>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="includePdfs"
                    checked={includePdfs}
                    onChange={this.handlePdfToggle}
                    disabled={submitting}
                  />
                  <label className="form-check-label" htmlFor="includePdfs">
                    Include PDF invoices as attachments
                  </label>
                </div>
              </div>
            </div>

            {submitting && <MASpinner />}

            <div className="mt-4 d-flex justify-content-end">
              <Button variant="secondary" onClick={onClose} disabled={submitting} className="mr-2">
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={submitting}>
                {submitting ? 'Sending...' : 'Send Hospital Chaser Email'}
              </Button>
            </div>
          </Form>
        </div>
      </Modal>
    );
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    form: 'HospitalBulkChaseForm',
    enableReinitialize: true,
    destroyOnUnmount: false,
  })(HospitalBulkChaseForm),
);
